import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { useSession } from "../../context/SessionContext";
import app, { systemAdministratorAuth } from "../../firebase";
import ResetPassword from "./users/ResetPassword";

const SIDEBAR_WIDTH = 240;
const HEADER_HEIGHT = 70;
const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;
const profileCache = new Map();

export default function AdminShell({ title, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, logout } = useSession();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [profileData, setProfileData] = useState(() => profileCache.get(currentUser?.uid) || {});
  const [profileLoadedFor, setProfileLoadedFor] = useState(() => (
    currentUser?.uid && profileCache.has(currentUser.uid) ? currentUser.uid : ""
  ));
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [savingImage, setSavingImage] = useState(false);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      if (!currentUser?.uid) {
        setProfileData({});
        setProfileLoadedFor("");
        return;
      }

      const cachedProfile = profileCache.get(currentUser.uid);
      if (cachedProfile) {
        setProfileData(cachedProfile);
        setProfileLoadedFor(currentUser.uid);
      } else {
        setProfileData({});
        setProfileLoadedFor("");
      }
      setProfileError("");
      try {
        const snapshot = await getDoc(doc(getFirestore(app), "users", currentUser.uid));
        if (active) {
          const nextProfile = snapshot.exists() ? snapshot.data() : {};
          profileCache.set(currentUser.uid, nextProfile);
          setProfileData(nextProfile);
          setProfileLoadedFor(currentUser.uid);
        }
      } catch (error) {
        console.error("Failed to load administrator profile", error);
        if (active) {
          setProfileError("Unable to load your profile details.");
          setProfileLoadedFor(currentUser.uid);
        }
      }
    };
    loadProfile();
    return () => { active = false; };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!selectedImage) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(selectedImage);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  const profileLoaded = !!currentUser?.uid && profileLoadedFor === currentUser.uid;
  const displayName = useMemo(() => {
    const storedName = profileData.fullName || profileData.name ||
      [profileData.firstName, profileData.middleName, profileData.lastName].filter(Boolean).join(" ").trim();
    if (!profileLoaded) return "";
    return storedName || currentUser?.displayName || currentUser?.email?.split("@")[0] || "";
  }, [currentUser?.displayName, currentUser?.email, profileData, profileLoaded]);
  const roleName = profileData.role || userRole || "System Administrator";
  const profileImageUrl = (profileLoaded && profileData.profilePicUrl) || currentUser?.photoURL ||
    "https://ui-avatars.com/api/?name=Administrator";

  const menuItems = [
    { key: "dashboard", label: "Dashboard", path: "/AdminDashboard" },
    { key: "users", label: "User Management", path: "/admin/users" },
    { key: "departments", label: "Departments", path: "/admin/departments" },
    { key: "announcements", label: "Announcements", path: "/admin/announcements" }
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin-login", { replace: true });
  };

  const handleImageSelection = (event) => {
    const image = event.target.files?.[0] || null;
    event.target.value = "";
    setProfileError("");
    if (!image) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(image.type)) {
      setProfileError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (image.size > MAX_PROFILE_IMAGE_SIZE) {
      setProfileError("Choose an image smaller than 5 MB.");
      return;
    }
    setSelectedImage(image);
  };

  const handleSaveProfileImage = async () => {
    if (!currentUser?.uid || !selectedImage) return;
    setSavingImage(true);
    setProfileError("");
    try {
      const imageRef = ref(getStorage(systemAdministratorAuth.app), `profiles/${currentUser.uid}/profile-picture`);
      await uploadBytes(imageRef, selectedImage, { contentType: selectedImage.type });
      const downloadUrl = await getDownloadURL(imageRef);
      await updateDoc(doc(getFirestore(app), "users", currentUser.uid), { profilePicUrl: downloadUrl });
      setProfileData(data => {
        const updatedProfile = { ...data, profilePicUrl: downloadUrl };
        profileCache.set(currentUser.uid, updatedProfile);
        return updatedProfile;
      });
      setSelectedImage(null);
    } catch (error) {
      console.error("Failed to save administrator profile picture", error);
      setProfileError("Unable to save your profile picture. Please try again.");
    } finally {
      setSavingImage(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#f4f6f8" }}>
      <aside style={{ width: SIDEBAR_WIDTH, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, zIndex: 1000 }}>
        <div style={{ padding: "32px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "center", marginBottom: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2 }}>EnrollEase</div>
        </div>
        <nav aria-label="Administrator navigation" style={{ paddingTop: 12, flex: 1 }}>
          {menuItems.map(item => {
            const active = location.pathname === item.path || (item.key === "users" && location.pathname.startsWith("/admin/users"));
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => navigate(item.path)}
                style={{
                  width: "calc(100% - 24px)",
                  padding: "14px 0",
                  paddingLeft: 32,
                  marginBottom: 8,
                  marginLeft: 0,
                  marginRight: 12,
                  border: 0,
                  textAlign: "left",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 17,
                  background: active ? "#e67e22" : "transparent",
                  color: "#fff",
                  display: "block",
                  transition: "background 0.2s, color 0.2s",
                  boxShadow: active ? "0 2px 8px #e67e22aa" : "none"
                }}
              >
                {item.label}
              </button>
            );
          })}
        </nav>
        <div style={{ padding: 16, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize: 12, opacity: 0.9 }}>v1.0 • Admin</div>
        </div>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH, minWidth: 0 }}>
        <header style={{ height: HEADER_HEIGHT, flexShrink: 0, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", boxSizing: "border-box", borderBottom: "1px solid #e6e9ec", position: "sticky", top: 0, zIndex: 999 }}>
          <div style={{ color: "#155c2b", fontWeight: 800, fontSize: 18 }}>{title || "System Administrator"}</div>
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontWeight: 700, color: "#155c2b", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
            <button
              type="button"
              aria-label="Open user menu"
              aria-expanded={profileMenuOpen}
              onClick={() => setProfileMenuOpen(open => !open)}
              style={{ width: 44, height: 44, padding: 0, border: "2px solid #ff6f2e", borderRadius: "50%", background: "#fff", cursor: "pointer", overflow: "hidden" }}
            >
              <img src={profileImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
            {profileMenuOpen && (
              <div style={{ position: "absolute", right: 0, top: HEADER_HEIGHT - 8, background: "#fff", borderRadius: 8, boxShadow: "0 6px 18px rgba(0,0,0,0.16)", minWidth: 190, padding: 8, zIndex: 1001 }}>
                <button type="button" onClick={() => { setProfileMenuOpen(false); setProfileError(""); setProfileOpen(true); }} style={menuActionStyle}>Profile</button>
                <button type="button" onClick={() => { setProfileMenuOpen(false); setPasswordResetOpen(true); }} style={menuActionStyle}>Change Password</button>
                <button type="button" onClick={handleLogout} style={{ ...menuActionStyle, color: "#e65100", fontWeight: 700 }}>Log out</button>
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: 24, boxSizing: "border-box", overflowY: "auto" }}>
          {children}
        </main>
      </div>

      {profileOpen && (
        <div role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) { setProfileOpen(false); setSelectedImage(null); } }} style={modalBackdropStyle}>
          <section role="dialog" aria-modal="true" aria-labelledby="admin-profile-title" style={modalCardStyle}>
            <h2 id="admin-profile-title" style={{ margin: "0 0 6px", color: "#155c2b" }}>Your Profile</h2>
            <p style={{ margin: "0 0 18px", color: "#666", fontSize: 14 }}>Change the profile picture shown in the administrator header.</p>
            <div style={{ textAlign: "center", marginBottom: 16, color: "#444", fontSize: 14 }}>
              <div style={{ fontWeight: 700 }}>{displayName}</div>
              <div>{roleName}</div>
            </div>
            <img src={previewUrl || profileImageUrl} alt="Profile preview" style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: "3px solid #e6e9ec", display: "block", margin: "0 auto 18px" }} />
            <label htmlFor="admin-profile-image" style={{ display: "block", fontWeight: 600, marginBottom: 6, color: "#333" }}>Profile picture</label>
            <input id="admin-profile-image" type="file" accept="image/*" onChange={handleImageSelection} disabled={savingImage} style={{ width: "100%", marginBottom: 12 }} />
            {profileError && <div role="alert" style={{ color: "#b00020", fontSize: 14, marginBottom: 12 }}>{profileError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setProfileOpen(false); setSelectedImage(null); setProfileError(""); }} disabled={savingImage} style={secondaryButtonStyle}>Cancel</button>
              <button type="button" onClick={handleSaveProfileImage} disabled={!selectedImage || savingImage} style={primaryButtonStyle}>{savingImage ? "Saving..." : "Save picture"}</button>
            </div>
          </section>
        </div>
      )}
      {passwordResetOpen && <ResetPassword email={currentUser?.email || ""} onClose={() => setPasswordResetOpen(false)} />}
    </div>
  );
}

const menuActionStyle = {
  display: "block",
  width: "100%",
  padding: "10px 12px",
  border: 0,
  borderRadius: 6,
  background: "#fff",
  textAlign: "left",
  color: "#333",
  cursor: "pointer"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  background: "rgba(0,0,0,0.4)"
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 420,
  padding: 24,
  borderRadius: 12,
  background: "#fff",
  boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
  boxSizing: "border-box"
};

const secondaryButtonStyle = {
  padding: "10px 14px",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  background: "#fff",
  color: "#333",
  cursor: "pointer"
};

const primaryButtonStyle = {
  padding: "10px 14px",
  border: 0,
  borderRadius: 8,
  background: "#155c2b",
  color: "#fff",
  cursor: "pointer"
};
